/**
 * An error response, turned into something a component can branch on.
 *
 * `ARCHITECTURE.md` §API conventions: "Errors are RFC 9457 problem details:
 * `{ type, title, status, detail, code, errors? }`… Clients see codes, never stack
 * traces"; §Frontend: "a `problem+json` renderer that shows the `title` and the
 * `code`, nothing more."
 *
 * Two failures, two types, because they are two different sentences to a person and
 * two different decisions for the code:
 *
 *   - `ApiProblem` — the server answered, and said no. It has a `code` from the
 *     closed union (`packages/shared/src/errors.ts`), a `status`, and the `req_id`
 *     that started a support conversation.
 *   - `NetworkError` — the request never got an answer: offline, DNS, a proxy that
 *     hung up. There is no code to read. A read asks again; a write says which of
 *     two it was — never sent (`NotSent`), or not known (`Unanswered`) — because only
 *     the first is known to have made nothing.
 *
 * A response that is neither — nginx's own 502 page, a gateway that answers HTML —
 * becomes an `ApiProblem` with `code: 'Internal'` and `fromBody: false`, so the
 * boundary still has a title and a status and nothing has to guess at a shape the
 * server did not send. **To a write, the proxy's own 502, 503 and 504 are no answer
 * at all** (`isTheProxysAnswer`).
 */

import {
  ProblemSchema,
  REPLAYED_REFUSAL_NOTE,
  type DomainErrorCode,
  type Problem,
} from '@fareast/shared'

/**
 * As much of a `Response` as this module reads.
 *
 * Hono's `ClientResponse<T>` is a structural interface rather than a subtype of
 * `Response` — it narrows `status` and `ok` to the literals the route declares —
 * so a parameter typed `Response` would not accept the thing the RPC client hands
 * back. This is the shape both have.
 */
export type AnyResponse = {
  readonly ok: boolean
  readonly status: number
  readonly statusText: string
  json(): Promise<unknown>
}

/** One field-level failure, keyed the way `packages/shared` schemas path them. */
export type FieldError = { readonly path: string; readonly message: string }

/** The flat facts a refusal chose to publish. `retry_after`, a seat limit, a scope. */
export type ProblemMeta = Readonly<Record<string, string | number | boolean | null>>

export class ApiProblem extends Error {
  override readonly name = 'ApiProblem'
  readonly title: string
  readonly status: number
  readonly code: DomainErrorCode
  readonly detail: string
  /** `instance` — the request id. Null when the answer was not a problem body. */
  readonly requestId: string | null
  readonly errors: readonly FieldError[]
  readonly meta: ProblemMeta
  /** False when the status was an error but the body was not RFC 9457. */
  readonly fromBody: boolean

  constructor(init: {
    title: string
    status: number
    code: DomainErrorCode
    detail: string
    requestId?: string | null
    errors?: readonly FieldError[]
    meta?: ProblemMeta
    fromBody?: boolean
  }) {
    super(init.detail === '' ? init.title : init.detail)
    this.title = init.title
    this.status = init.status
    this.code = init.code
    this.detail = init.detail
    this.requestId = init.requestId ?? null
    this.errors = init.errors ?? []
    this.meta = init.meta ?? {}
    this.fromBody = init.fromBody ?? true
  }

  /** The one field errors belong to, for a form resolver. */
  errorFor(path: string): string | undefined {
    return this.errors.find((entry) => entry.path === path)?.message
  }
}

export class NetworkError extends Error {
  override readonly name: string = 'NetworkError'
  constructor(
    cause: unknown,
    message = 'The portal could not be reached. Check your connection and try again.',
  ) {
    super(message, { cause })
  }
}

/** The four sentences of a write of which nothing is known: what happened, and where to look. */
const UNANSWERED_SAID = {
  silent: {
    here:
      'No answer came back, so this may or may not have been done. Look at how things ' +
      'stand before you try it again.',
    reload:
      'No answer came back, so this may or may not have been done. Reload the page to see ' +
      'how things stand before you try it again.',
  },
  unreached: {
    here:
      'The portal could not be reached, so this may or may not have been done. Check ' +
      'your connection, and look at how things stand before you try it again.',
    reload:
      'The portal could not be reached, so this may or may not have been done. Check ' +
      'your connection, and reload the page to see how things stand before you try it again.',
  },
} as const

/**
 * A write of which **nothing is known**: it was sent, or may have been, and no answer
 * of the application's came back (`api/hooks.ts` `useApiMutation`).
 *
 * A `NetworkError`, because everything that is true of a request that did not arrive
 * is true of this one — it is not a refusal — and one thing more, which is why it has
 * its own sentences: it **may have been made**. Neither of them says "try again".
 *
 * `'silent'`: it went, and nothing came back in the time one is waited for, or what
 * came back was the proxy's own page, or a yes that could not be read. `'unreached'`:
 * the browser says it did not arrive — which a browser says in the same words of a
 * request that never left and of one whose answer was lost on the way back, so the
 * sentence is about the connection and still does not say it was not made.
 *
 * **And it says where to look, truthfully** (`look`). `'here'`: the page is being read
 * again for it. `'reload'`: it is not — a read that is not `live` is left as it is
 * after a write nothing answered — and "look at how things stand" over a page that
 * was knowingly not read was an invitation to conclude that it had not been done.
 */
export class Unanswered extends NetworkError {
  override readonly name = 'Unanswered'
  readonly how: 'silent' | 'unreached'
  constructor(
    how: 'silent' | 'unreached' = 'silent',
    cause?: unknown,
    look: 'here' | 'reload' = 'here',
  ) {
    super(cause, UNANSWERED_SAID[how][look])
    this.how = how
  }
}

/**
 * A write that was **not sent**: the browser said it was off line, and the write said
 * so instead of going (`api/hooks.ts` `useApiMutation`). The one `NetworkError` of
 * which it is known that nothing was made.
 */
export class NotSent extends NetworkError {
  override readonly name = 'NotSent'
  constructor() {
    super(new Error('The browser is off line.'))
  }
}

export function isApiProblem(error: unknown): error is ApiProblem {
  return error instanceof ApiProblem
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError
}

/**
 * What the door refuses with before it looks at the call, **and which the reader gets
 * past by doing the same thing again**: nobody signed in, too many requests. A `403` is
 * not one: a service refuses with it too, which is an answer to the call, and one the
 * same call gets again — held as "before the call", it never settled the first try.
 */
const BEFORE_THE_CALL: ReadonlySet<number> = new Set([401, 429])

/**
 * True for a refusal **of the call itself** — the application's own, and not one it
 * gives before it has looked at the call (`BEFORE_THE_CALL`).
 *
 * Under an earlier try's idempotency key that is what became of the earlier try as
 * well — the door answers a key it has an answer to from its store, the stored answer
 * included — so the earlier one made nothing. A `401` says only that the session has
 * ended since, and the reader who signs in again and presses again is still asking
 * after the first.
 */
export function isOfTheCallItself(problem: ApiProblem): boolean {
  return problem.fromBody && problem.status < 500 && !BEFORE_THE_CALL.has(problem.status)
}

/** What a proxy answers with for an application that did not: nothing upstream, or nothing in time. */
const PROXY_ANSWERS: ReadonlySet<number> = new Set([502, 503, 504])

/**
 * True for an error answer that is **the proxy's and not the application's**: a `502`,
 * a `503` or a `504` whose body is not a problem document.
 *
 * nginx answers for an upstream it could not reach, or that had not answered in its
 * sixty seconds (`infra/nginx/portal.conf` `proxy_read_timeout`), with a page of its
 * own — there is no `error_page` to dress it. To a read that is one more failure to
 * ask through. To a **write** it is no answer at all: the handler may have committed,
 * or may yet, and `api/hooks.ts` `useApiMutation` says it as `Unanswered`. The
 * application's own `503` is a problem document, and is its answer. So is every other
 * status the proxy can give by itself — a `413`, a `429` — which it gives *instead* of
 * passing the request on.
 */
export function isTheProxysAnswer(error: unknown): boolean {
  return isApiProblem(error) && !error.fromBody && PROXY_ANSWERS.has(error.status)
}

/**
 * True for the proxy's answer that says the application **is there and was slow** — a
 * `504`, its sixty seconds run out — and not that it could not be reached at all: a
 * `502` or a `503`, which is what a restart looks like. The first can be asked what
 * became of things straight away; the second cannot.
 */
export function isSlowNotGone(error: unknown): boolean {
  return isTheProxysAnswer(error) && isApiProblem(error) && error.status === 504
}

/**
 * A response that is not 2xx, as an `ApiProblem`.
 *
 * The body is read once and parsed against the same schema the API renders with, so
 * a shape neither side agreed on cannot reach a component. `Content-Type` is not
 * the test — a proxy rewrites it, and the schema is a better check than a header.
 */
export async function problemOf(response: AnyResponse): Promise<ApiProblem> {
  const body = await readJson(response)
  const parsed = ProblemSchema.safeParse(body)

  if (!parsed.success) return unparseable(response)

  const problem: Problem = parsed.data
  return new ApiProblem({
    title: problem.title,
    status: problem.status,
    code: problem.code,
    detail: problem.detail,
    requestId: problem.instance ?? null,
    errors: problem.errors ?? [],
    meta: problem.meta ?? {},
  })
}

/**
 * What a refused write says, in one sentence, to the person who made it.
 *
 * `ARCHITECTURE.md` §Frontend's "show the `title` and the `code`, nothing more" is
 * the **route boundary** — a failure nobody expected. A refusal somebody can act on
 * is a different thing: `detail` is the sentence the API wrote for them ("Only an
 * owner or an admin can change who is on the account"), and it is the whole message.
 *
 * `ApiProblem` extends `Error`, so `error.message` compiles everywhere and is the
 * generic title rather than the detail. Session 19 shipped with six copies of the
 * failure notice and three derivations of its text: a refused role change showed the
 * API's own sentence and a refused approval, top-up, booking or cancellation did
 * not. This is the one derivation.
 */
export function messageOf(error: unknown): string {
  const made = heldCallAlreadyMade(error)
  if (made !== null) return made
  if (isApiProblem(error)) {
    // Answered from the store: the refusal, without the note for a tool caller.
    const note = ` ${REPLAYED_REFUSAL_NOTE}`
    const detail =
      error.meta['replayed'] === true && error.detail.endsWith(note)
        ? error.detail.slice(0, -note.length)
        : error.detail
    return detail === '' ? error.title : detail
  }
  if (error instanceof Error) return error.message
  return 'That did not go through. Try again, and tell us if it keeps happening.'
}

/**
 * The one refusal whose own sentence is written for a tool caller and is met by a
 * person on a page: a call an agent was not allowed to make was held as a review
 * task, somebody has since made it, and the same call is being made again.
 *
 * The API says to "call the tool under an idempotency_key no earlier call has used",
 * which is true and is nothing a person pressing Charge on an invoice can do. What
 * they can do is the first half of that sentence: close the review. `meta.ran_at`
 * with no `meta.status` is that refusal and no other — a held call named from a task
 * that has been closed carries the status, and its own sentence is the right one.
 */
function heldCallAlreadyMade(error: unknown): string | null {
  if (!isApiProblem(error) || error.code !== 'Conflict') return null
  const meta = error.meta
  const ref = meta['task_ref']
  if (typeof ref !== 'string' || typeof meta['ran_at'] !== 'string' || 'status' in meta) {
    return null
  }
  return (
    `This was held for review as ${ref}, and it has already been made from there. ` +
    `It is not made twice while that review is open: complete or cancel ${ref}, ` +
    'and it can be made again.'
  )
}

/**
 * Session expiry is the one status the SPA acts on rather than renders.
 * `.docs/08-web-foundation.md` §Edge cases: the next query returns 401 and the
 * boundary redirects to `/login`, once.
 */
export function isUnauthenticated(error: unknown): boolean {
  return isApiProblem(error) && error.status === 401
}

/**
 * The fields a refusal says **somebody else changed first**, or null when it is not
 * that refusal.
 *
 * A change that says what it was made from (`if_was` — on `tasks_update`, on
 * `tasks_assign` and on every move a person makes) is refused with a `409 Conflict`
 * naming the fields that no longer
 * hold it, in `meta.changed`, comma-separated — `meta` is scalars only. The API's own
 * sentence is written for a tool caller ("read the task again and decide"); a page
 * has the task on screen and says it in its own words, which is why this hands back
 * the names and not a message.
 *
 * Null for a `Conflict` that names none: the whole-task stamp's refusal
 * (`if_unchanged_since`) is the same code and a different fact.
 */
export function changedByOthers(error: unknown): readonly string[] | null {
  if (!isApiProblem(error) || error.status !== 409 || error.code !== 'Conflict') return null
  const changed = error.meta['changed']
  if (typeof changed !== 'string' || changed === '') return null
  return changed.split(',')
}

/* --------------------------------------------------- a refusal, onto the fields */

/**
 * A refusal's field-level paths, whichever way the API carried them.
 *
 * `ARCHITECTURE.md` §Frontend: "Errors from the API's `errors` field map back onto
 * fields." Two shapes say the same thing and a form has to read both:
 *
 *   - **`errors: [{ path, message }]`** — what `http/problem.ts` renders a `ZodError`
 *     as, one entry per issue, each with its own sentence;
 *   - **`meta.path`** — what a hand-raised `DomainError` carries. A service that
 *     refuses one key names it there and puts the sentence in `detail`, which is
 *     `messageOf`. `usage.setConfigByClient`'s two refusals are both this shape.
 *
 * The second is a **fallback**, not an addition: a `ZodError` that also happened to
 * carry a `meta.path` would otherwise say the same thing twice, once per field and
 * once more on the field the meta named.
 */
export function problemPaths(error: unknown): readonly FieldError[] {
  if (!isApiProblem(error)) return []
  if (error.errors.length > 0) return error.errors
  const path = error.meta['path']
  return typeof path === 'string' ? [{ path, message: messageOf(error) }] : []
}

/**
 * Those of them a form can actually show, with the action's own field unnested.
 *
 * **Narrowed to the controls that exist**, and that is not tidiness: setting an error
 * on a field react-hook-form does not know about blocks every later submit with a
 * message nobody can see and no box to clear it in. A dotted path is kept whole past
 * its first segment (`amount.currency`), because that is a real control —
 * `MoneyField` binds `amount.major` and `amount.currency` separately.
 *
 * `under` is for a form whose controls are one level **inside** the input the server
 * validated: the studio's widget-configuration form draws `config`'s properties and
 * the API refuses `config.site_daily`, and the client's own form is shown paths the
 * service spells the same way for the same reason. Without the strip the refusal
 * lands on no control at all and shows only as the sentence at the foot of the form.
 */
export function fieldErrorsOf(
  error: unknown,
  rendered: ReadonlySet<string>,
  under?: string,
): readonly FieldError[] {
  const prefix = under === undefined ? null : `${under}.`
  return problemPaths(error)
    .map((one) => ({
      path:
        prefix !== null && one.path.startsWith(prefix) ? one.path.slice(prefix.length) : one.path,
      message: one.message,
    }))
    .filter((one) => rendered.has(one.path.split('.')[0] ?? one.path))
}

/* ------------------------------------------------------------------- internals */

async function readJson(response: AnyResponse): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    return null
  }
}

function unparseable(response: AnyResponse): ApiProblem {
  return new ApiProblem({
    title: response.statusText === '' ? 'Request failed' : response.statusText,
    status: response.status,
    code: 'Internal',
    detail:
      'The portal answered with something it does not normally send. Try again, and tell us ' +
      'if it keeps happening.',
    fromBody: false,
  })
}
